/**
 * RevenueCat Web Billing client (singleton).
 *
 * The public Web Billing API key (`rcb_…`) is fetched from the Supabase
 * `get-service-config` Edge Function — never read from `.env.local` or
 * any repo file. The user's contract is: API keys live in Supabase
 * secrets, served through `get-service-config`, fetched at runtime.
 *
 * The SDK is browser-only — guard any caller with
 * `typeof window !== 'undefined'` or initialise inside a `useEffect`,
 * never at module top-level on the server.
 *
 * App User ID source order:
 *   1. Supabase auth user id (logged-in case) — passed in by the caller
 *   2. Anonymous id auto-generated by Purchases.configure() (cold visitor
 *      who taps a pricing CTA before signing in)
 *
 * Anonymous purchases get aliased to the real user id via Purchases.changeUser()
 * once the visitor finishes the sign-up flow.
 */

import { Purchases, type Offerings, type Package } from "@revenuecat/purchases-js";
import { createClient } from "@/lib/supabase/client";

/** Maps each LinkBox tier to the RC offering identifier. Mirrors the iOS
 *  `LinkBoxPaywallOffering` enum so the same identifiers route through
 *  the dashboard regardless of platform. */
export const RC_OFFERING_IDS = {
  pro: null, // RC's `default` offering — passing null = current
  proHolo: "pro_holo",
  team: "team",
  linkcoder: "linkcoder",
} as const;

export type LinkBoxPlanKey = keyof typeof RC_OFFERING_IDS;

let purchasesInstance: Purchases | null = null;
let cachedPublicKey: string | null = null;

/** Fetches the RC Web Billing public key from get-service-config. The
 *  authenticated tier returns it for any signed-in or anon-keyed caller.
 *  Cached in-memory for the page lifetime — RC keys rotate via dashboard,
 *  so a hard reload picks up new values. */
async function fetchPublicKey(): Promise<string> {
  if (cachedPublicKey) return cachedPublicKey;
  const supabase = createClient();
  const { data, error } = await supabase.functions.invoke("get-service-config");
  if (error) {
    throw new Error(
      `Failed to fetch service config for RevenueCat key: ${error.message}`
    );
  }
  const key = (data as Record<string, string> | null)?.revenuecat_web_billing_public_key;
  if (!key) {
    throw new Error(
      "revenuecat_web_billing_public_key missing from get-service-config. " +
        "Set it in Supabase secrets: " +
        "`supabase secrets set REVENUECAT_WEB_BILLING_PUBLIC_KEY=rcb_… " +
        "--project-ref sjsbqmmqnywvsrdmzoyh` and redeploy the edge function."
    );
  }
  cachedPublicKey = key;
  return key;
}

/** Lazy-initialised Purchases singleton. Pass the Supabase user id when
 *  available; an anonymous id is used until the user signs in. */
export async function getPurchases(appUserId?: string): Promise<Purchases> {
  if (typeof window === "undefined") {
    throw new Error("RevenueCat purchases-js is browser-only.");
  }
  if (purchasesInstance) {
    if (appUserId && appUserId !== Purchases.getSharedInstance()?.getAppUserId()) {
      // User signed in mid-session — alias the anonymous id to the real one.
      void purchasesInstance.changeUser(appUserId);
    }
    return purchasesInstance;
  }
  const apiKey = await fetchPublicKey();
  purchasesInstance = Purchases.configure(
    apiKey,
    appUserId ?? Purchases.generateRevenueCatAnonymousAppUserId()
  );
  return purchasesInstance;
}

/** Loads RC offerings and returns the package matching the requested tier
 *  + billing cycle. Returns null if the offering or package isn't published. */
export async function findPackage(
  plan: LinkBoxPlanKey,
  cycle: "monthly" | "annual",
  appUserId?: string
): Promise<Package | null> {
  const purchases = await getPurchases(appUserId);
  const offerings: Offerings = await purchases.getOfferings();
  const offeringId = RC_OFFERING_IDS[plan];
  const offering = offeringId ? offerings.all[offeringId] : offerings.current;
  if (!offering) return null;
  // RC's package identifiers: $rc_monthly / $rc_annual / $rc_lifetime / etc.
  const packageId = cycle === "annual" ? "$rc_annual" : "$rc_monthly";
  return offering.availablePackages.find((p) => p.identifier === packageId) ?? null;
}

/** Initiates RC's hosted Web Billing checkout for the given package. The
 *  SDK opens a redirect / new-window flow per its config; on success the
 *  customer's RC entitlement updates server-side and the iOS / Android
 *  apps see the new entitlement on next refresh. */
export async function purchase(
  plan: LinkBoxPlanKey,
  cycle: "monthly" | "annual",
  appUserId?: string
): Promise<void> {
  const purchases = await getPurchases(appUserId);
  const pkg = await findPackage(plan, cycle, appUserId);
  if (!pkg) {
    throw new Error(
      `No RC package found for plan="${plan}" cycle="${cycle}". ` +
        "Verify the offering exists in the RC dashboard and has the requested package."
    );
  }
  await purchases.purchase({ rcPackage: pkg });
}
